"use client"

import { useState } from "react"

import { cn } from "@/common/lib/utils"
import type { Obligation } from "@/modules/lernpfad/engine"
import type { Translator } from "@/modules/lernpfad/i18n/ui"
import { STATUS_VAR } from "./palette"

/**
 * Obligation status per team member, as a compact matrix.
 *
 * Form choice: two categorical dimensions (person × duty) with a small ordinal
 * state per cell. A matrix is the only form that shows both "who is exposed" and
 * "which duty is systematically slipping" at once — a per-person bar answers the
 * first and hides the second, which is the question a manager actually opens
 * this view with.
 *
 * State is carried by fill AND by a glyph, never colour alone.
 */

export type CellState = "ok" | "due" | "overdue" | "n/a"

const GLYPH: Record<CellState, string> = { ok: "✓", due: "•", overdue: "!", "n/a": "" }

const FILL: Record<CellState, string> = {
    ok: STATUS_VAR.good,
    due: STATUS_VAR.warning,
    overdue: STATUS_VAR.critical,
    "n/a": "transparent",
}

export interface TeamStatusRow {
    id: string
    name: string
    cells: Record<string, CellState>
}

interface TeamStatusStripProps {
    rows: TeamStatusRow[]
    obligations: Obligation[]
    t: Translator
    obligationLabel: (id: string, fallback: string) => string
    onSelect?: (id: string) => void
    selectedId?: string
}

export default function TeamStatusStrip({
    rows,
    obligations,
    t,
    obligationLabel,
    onSelect,
    selectedId,
}: TeamStatusStripProps) {
    const [hover, setHover] = useState<{ row: string; col: string } | null>(null)

    // Only duties that bind at least one person — an all-"n/a" column is noise.
    const columns = obligations.filter((obligation) =>
        rows.some((row) => row.cells[obligation.id] && row.cells[obligation.id] !== "n/a"),
    )
    if (columns.length === 0 || rows.length === 0) return null

    const hoveredLabel = hover
        ? obligationLabel(
              hover.col,
              columns.find((column) => column.id === hover.col)?.label ?? "",
          )
        : null

    return (
        <figure className="m-0">
            <div className="overflow-x-auto">
                <div className="min-w-[420px]">
                    {rows.map((row) => (
                        <div
                            key={row.id}
                            className={cn(
                                "flex items-center gap-1 rounded-md px-1 py-1 transition-colors",
                                onSelect && "cursor-pointer",
                                selectedId === row.id ? "bg-muted/70" : "hover:bg-muted/40",
                            )}
                            onClick={() => onSelect?.(row.id)}
                        >
                            <span className="w-[38%] shrink-0 truncate text-xs">{row.name}</span>
                            <div className="flex flex-1 gap-1">
                                {columns.map((column) => {
                                    const state = row.cells[column.id] ?? "n/a"
                                    return (
                                        <span
                                            key={column.id}
                                            className={cn(
                                                "flex h-5 flex-1 items-center justify-center rounded-[3px] text-[9px] font-bold text-white transition-transform",
                                                state === "n/a" && "border border-dashed opacity-30",
                                                hover?.col === column.id && "scale-y-110",
                                            )}
                                            style={{ background: FILL[state] }}
                                            onMouseEnter={() => setHover({ row: row.id, col: column.id })}
                                            onMouseLeave={() => setHover(null)}
                                            title={obligationLabel(column.id, column.label)}
                                        >
                                            {GLYPH[state]}
                                        </span>
                                    )
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <figcaption className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
                <span className="inline-flex items-center gap-1.5">
                    <span
                        className="flex size-3 items-center justify-center rounded-[2px] text-[8px] font-bold text-white"
                        style={{ background: FILL.ok }}
                    >
                        ✓
                    </span>
                    {t("manager.fulfilled")}
                </span>
                <span className="inline-flex items-center gap-1.5">
                    <span
                        className="flex size-3 items-center justify-center rounded-[2px] text-[8px] font-bold text-white"
                        style={{ background: FILL.due }}
                    >
                        •
                    </span>
                    {t("manager.openBadge", { count: "" }).trim()}
                </span>
                <span className="inline-flex items-center gap-1.5">
                    <span
                        className="flex size-3 items-center justify-center rounded-[2px] text-[8px] font-bold text-white"
                        style={{ background: FILL.overdue }}
                    >
                        !
                    </span>
                    {t("item.overdue")}
                </span>
                {hoveredLabel && <span className="ml-auto font-medium">{hoveredLabel}</span>}
            </figcaption>
        </figure>
    )
}
